import { useCallback, useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useDisruptionsOverview } from '@/lib/disruptions/use-disruptions';
import { fetchDisruption } from '@/lib/disruptions/api';
import type {
  DisruptionCaseRow,
  DisruptionDetail,
  DisruptionOverviewRow,
} from '@/lib/disruptions/types';
import { useAirportScope } from '@/lib/operator-context/airport-scope';
import { joinDisruption, leaveDisruption } from '@/lib/ws/socket';
import { useNow } from '@/components/allocation/use-now';
import {
  boardKpis,
  groupByDay,
  nextOnBoard,
  routeLookupUrns,
  type DayGroups,
  type IBoardKpis,
  type IBoardNext,
} from './board-model';
import { rowInAirports, rowInScope, type DisruptionsScope } from './situation';

// The OPEN BOARD's data — what the disruptions list (/disruptions) and the operator's
// home (/dashboard, «Resumen») both read of «what is happening now», so their numbers
// and «Lo que sigue» never disagree: the overview narrowed to a scope lens and the
// header airport scope, grouped by day (`groupByDay`), its KPI (`boardKpis`), «Lo que
// sigue» (`nextOnBoard`), the flights' routes and the live rooms.
//
// Data: the ONE `overview` query, kept live by WS pushes (join the rooms of the newest
// open rows; a push invalidates the overview — no polling, §18 / NEX-293). The route of
// each flight is not on the overview row: the detail's cases carry it, so the board
// reads the detail of today's rows and, when all of it fits, yesterday's (capped,
// whole groups only — `routeLookupUrns`) — the SAME cache Operar reads, so opening a
// row then lands instantly.

/** Rooms joined for live pushes — the newest open rows only, so an operator with a wall
 *  of disruptions doesn't fan the broadcaster out across every one. */
const MAX_LIVE_ROOMS = 8;

/** Details read for the flights' routes — every «Hoy» row up to this, then «Ayer» when
 *  all of it fits (`routeLookupUrns`). Past the cap (a day with more than 16
 *  disruptions) the oldest «Hoy» rows keep the fallback title: their scope's flight
 *  codes, else their title. A backend route on the overview row would retire this. */
const MAX_ROUTE_LOOKUPS = 16;

/** The day groups and the ages re-read the clock once a minute (midnight moves «Hoy»). */
const CLOCK_TICK_MS = 60_000;

export interface IOpenBoard {
  overview: ReturnType<typeof useDisruptionsOverview>;
  /** Every overview row, before any scope — the lens's airport / country options. */
  allRows: DisruptionOverviewRow[];
  /** The header airport scope (NEX-310) the rows were narrowed by. */
  headerAirports: ReturnType<typeof useAirportScope>['airports'];
  groups: DayGroups<DisruptionOverviewRow>;
  kpis: IBoardKpis;
  /** «Lo que sigue» — `null` when nothing qualifies or the board is off. */
  next: IBoardNext | null;
  /** The cached detail's cases per disruption URN — the route of each flight. */
  casesByUrn: ReadonlyMap<string, readonly DisruptionCaseRow[]>;
  /** Disruptions whose detail is being read right now — their title waits for it. */
  pendingUrns: ReadonlySet<string>;
  /** The clock the groups were cut at (re-read once a minute while on). */
  now: number;
}

/**
 * The open board's data. `enabled: false` (the past tab) stops the overview, the clock,
 * the live rooms and the route reads; the header airport scope always applies and
 * composes with the lens (both must match).
 */
export function useOpenBoard({
  enabled,
  scope,
}: {
  enabled: boolean;
  scope: DisruptionsScope;
}): IOpenBoard {
  const { airports: headerAirports } = useAirportScope();
  const overview = useDisruptionsOverview(enabled);
  const allRows = useMemo(() => overview.data?.rows ?? [], [overview.data]);
  const scopedRows = useMemo(
    () => allRows.filter((row) => rowInScope(row, scope) && rowInAirports(row, headerAirports)),
    [allRows, scope, headerAirports],
  );
  const now = useNow(enabled, CLOCK_TICK_MS);
  const groups = useMemo(() => groupByDay(scopedRows, now), [scopedRows, now]);
  const kpis = useMemo(() => boardKpis(groups), [groups]);

  // Live: join the newest open rows' rooms; any progress push invalidates the overview
  // (apply-event.ts) — push-driven, no timer. Keyed by the URN list, so the minute
  // tick never leaves and re-joins the rooms.
  const liveKey = [...groups.today, ...groups.yesterday, ...groups.stale]
    .filter((r) => r.severity !== 'settled')
    .slice(0, MAX_LIVE_ROOMS)
    .map((r) => r.urn)
    .join('|');
  useEffect(() => {
    if (!enabled || !liveKey) return;
    const urns = liveKey.split('|');
    for (const urn of urns) joinDisruption(urn);
    return () => {
      for (const urn of urns) leaveDisruption(urn);
    };
  }, [liveKey, enabled]);

  // The flights' routes: the detail of the rows `routeLookupUrns` picks — the same key
  // and fetcher as `useDisruption`, so Operar reuses it. Keyed by the URN list, not by
  // `groups` (a new object every clock tick). While a read is in flight its row holds
  // the title's place (no title → flight-code jump on load).
  const lookupKey = enabled ? routeLookupUrns(groups, MAX_ROUTE_LOOKUPS).join('|') : '';
  const lookupUrns = useMemo(() => (lookupKey ? lookupKey.split('|') : []), [lookupKey]);
  const combineCases = useCallback(
    (results: { data?: readonly DisruptionCaseRow[] | undefined; isPending: boolean }[]) => {
      const cases = new Map<string, readonly DisruptionCaseRow[]>();
      const pending = new Set<string>();
      results.forEach((q, i) => {
        const urn = lookupUrns[i]!;
        if (Array.isArray(q.data)) cases.set(urn, q.data);
        else if (q.isPending) pending.add(urn);
      });
      return { cases, pending };
    },
    [lookupUrns],
  );
  const lookups = useQueries({
    queries: lookupUrns.map((urn) => ({
      queryKey: ['disruptions', urn],
      queryFn: () => fetchDisruption(urn),
      staleTime: 30_000,
      select: (detail: DisruptionDetail) => detail?.cases,
    })),
    combine: combineCases,
  });

  return {
    overview,
    allRows,
    headerAirports,
    groups,
    kpis,
    next: enabled ? nextOnBoard(groups) : null,
    casesByUrn: lookups.cases,
    pendingUrns: lookups.pending,
    now,
  };
}
